import { useCallback, useRef, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useToast } from '@/hooks/useToast';
import { loadDriver } from '../tour/loadDriver';
import { resolveTourElement } from '../tour/resolveTourElement';
import { renderTourStep } from '../tour/renderTourStep';
import type { HelpStep, HelpTourId } from '../vacationHelp.types';

type TourTarget = HTMLElement | (() => HTMLElement | null);

function resolveDemoTarget(elementId: string): () => HTMLElement | null {
    return () => document.querySelector<HTMLElement>(`[data-tour="${elementId}"]`);
}

export function useVacationTour(
    tourId: HelpTourId,
    steps: HelpStep[],
    userEmail?: string,
    ready: boolean = true,
    autoStart: boolean = false,
    onTourStart?: () => void,
    onTourEnd?: () => void,
    onDemoChange?: (demo: string | null) => void
) {
    const driverInstance = useRef<any>(null);
    const [isReady, setIsReady] = useState(false);
    const toast = useToast();
    const tourStartedKey = `vacation-tour-seen:v1:${tourId}:${userEmail || 'unknown'}`;

    useEffect(() => {
        setIsReady(ready);
    }, [ready]);

    const startTour = useCallback(async () => {
        if (!isReady) return;

        flushSync(() => onTourStart?.());

        const driverModule = await loadDriver();
        if (!driverModule) return;

        const validSteps = steps.flatMap((step): { step: HelpStep; element: TourTarget }[] => {
            if (step.demo) return [{ step, element: resolveDemoTarget(step.element) }];
            const element = resolveTourElement(step.element);
            return element ? [{ step, element }] : [];
        });

        if (validSteps.length === 0) {
            toast.info('Abra esta tela com dados carregados para ver o tour.');
            onTourEnd?.();
            return;
        }

        const hasDemo = validSteps.some(({ step }) => step.demo);
        const lastIndex = validSteps.length - 1;

        const goToStep = (index: number) => {
            flushSync(() => onDemoChange?.(validSteps[index].step.demo ?? null));
            driverInstance.current?.moveTo(index);
        };

        driverInstance.current = driverModule.driver({
            animate: true,
            duration: 300,
            smoothScroll: true,
            allowClose: true,
            allowKeyboardControl: !hasDemo,
            allowScroll: true,
            overlayClickBehavior: 'close',
            disableActiveInteraction: true,
            overlayColor: '#161343',
            overlayOpacity: 0.35,
            stagePadding: 8,
            stageRadius: 14,
            popoverOffset: 14,
            popoverClass: 'vacation-tour',
            showProgress: false,
            prevBtnText: '← Voltar',
            nextBtnText: 'Próximo →',
            doneBtnText: 'Concluir',
            closeBtnLabel: 'Fechar',
            onDestroyed: () => {
                onDemoChange?.(null);
                onTourEnd?.();
            },
            steps: validSteps.map(({ step, element }, idx) => ({
                element,
                popover: {
                    title: step.title,
                    description: renderTourStep(step, idx + 1, validSteps.length),
                    side: (step.side || 'bottom') as 'top' | 'bottom' | 'left' | 'right',
                    align: (step.align || 'center') as 'start' | 'center' | 'end',
                    ...(hasDemo && {
                        onNextClick: () => {
                            if (idx === lastIndex) {
                                driverInstance.current?.destroy();
                                return;
                            }
                            goToStep(idx + 1);
                        },
                        onPrevClick: () => {
                            if (idx > 0) goToStep(idx - 1);
                        },
                    }),
                },
            })),
        });

        flushSync(() => onDemoChange?.(validSteps[0].step.demo ?? null));
        driverInstance.current.drive();

        try {
            localStorage.setItem(tourStartedKey, 'true');
        } catch (e) {
            // localStorage pode estar indisponível
        }
    }, [isReady, steps, tourId, tourStartedKey, toast, onTourStart, onTourEnd, onDemoChange]);

    const checkFirstVisit = useCallback(() => {
        try {
            return !localStorage.getItem(tourStartedKey);
        } catch (e) {
            return false;
        }
    }, [tourStartedKey]);

    const closeTour = useCallback(() => {
        if (driverInstance.current) {
            driverInstance.current.destroy();
            driverInstance.current = null;
        }
    }, []);

    useEffect(() => {
        if (autoStart && isReady && checkFirstVisit()) {
            const timer = setTimeout(() => {
                startTour();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [autoStart, isReady, checkFirstVisit, startTour]);

    useEffect(() => {
        return () => {
            closeTour();
        };
    }, [closeTour]);

    return {
        startTour,
        closeTour,
        checkFirstVisit,
    };
}
